import constants from './constants';

const PAGING_DEFAULTS = {
  itemsPerPage: 25,
  page: 1,
  sortBy: [],
  sortDesc: []
};

function defaultTo (value, defaultValue)
{
  let result = defaultValue;
  if (value !== undefined && value !== null)
  {
    result = value;
  }

  return result;
}

function defaultToNumber (value, defaultValue)
{
  return isNaN(value) ? defaultValue : parseInt(value);
}

export default class Pagination
{
  /**
   * Static utility to quickly generate paging headers used to make a max result paging request. Optionally allows for
   * specifying sorting information.
   *
   * @param {String} sortBy
   * @param {String} sortDirection
   */
  static generateMaxHeaders (sortBy, sortDirection = 'asc')
  {
    let headers = {
      'X-CTrack-Paging-StartIndex': 1,
      'X-CTrack-Paging-MaxResults': constants.MAX_API_RESULTS_LIMIT
    };

    if (sortBy)
    {
      headers['X-CTrack-Paging-OrderBy'] = sortBy + ' ' + sortDirection;
    }

    return headers;
  }

  /**
	 * Standard constructor
	 *
	 * @param options [Object] an object allowing for overrides of the default paging settings
	 */
  constructor (options = {})
  {
    this.itemsPerPage = defaultToNumber(options.itemsPerPage, PAGING_DEFAULTS.itemsPerPage);
    this.page = defaultToNumber(options.page, PAGING_DEFAULTS.page);
    this.sortBy = defaultTo(options.sortBy, PAGING_DEFAULTS.sortBy);
    this.sortDesc = defaultTo(options.sortDesc, PAGING_DEFAULTS.sortDesc);
    this.totalItems = 0;
    this.accessibleItems = 0;
  }

  /**
	 * Converts this paging state's current state into a set of paging headers for a request.
	 *
	 * @returns {Object}
	 */
  generateHeaders ()
  {
    let headers = {
      'X-CTrack-Paging-StartIndex': ((this.page - 1) * this.itemsPerPage) + 1,
      'X-CTrack-Paging-MaxResults': this.itemsPerPage,
      'X-CTrack-Paging-CalculateTotalCount': true
    };

    // the server cannot handle null sort values, so only set this header if a sort was specified
    if (this.sortBy[0] !== undefined && this.sortBy[0] !== null)
    {
      headers['X-CTrack-Paging-OrderBy'] = this.sortBy[0] + ' ' + (this.sortDesc[0] ? 'desc' : 'asc');
    }

    return headers;
  }

  /**
	 * Parses the response headers from the specified AJAX call and updates this paging state
	 * to reflect the data in the response.
	 *
	 * @param response [Object] the AJAX response object
	 */
  parseHeaders (response)
  {
    this.totalItems = parseInt(response.headers['x-ctrack-paging-totalcount']);
    this.accessibleItems = this.totalItems;

    let limitHeader = response.headers['x-ctrack-paging-resultslimit'];
    if (limitHeader !== undefined && limitHeader !== null)
    {
      let maxResultsAllowed = parseInt(limitHeader);
      if (maxResultsAllowed > 0 && this.accessibleItems > maxResultsAllowed)
      {
        this.accessibleItems = maxResultsAllowed;
        this.accessibleItems -= this.accessibleItems % this.itemsPerPage;
      }
    }
  }

  /**
   * Returns an object representing this internal state of this paging object so it can be encoded into the URL and
   * decoded at some later point.
   *
   * @returns [Object]
   */
  generateURLParams ()
  {
    let params = {
      'p.page': this.page
    };

    if (this.sortBy[0] !== undefined && this.sortBy[0] !== null)
    {
      params['p.sortBy'] = this.sortBy[0];
      params['p.sortDesc'] = this.sortDesc[0];
    }

    return params;
  }

  /**
   * Used as callback / sync operation when vuetify detects a paging change. Updates the internal state of our paging object to reflect the
   * change. Sometimes we get false updates from vuetify so we'll manually check to see if anything actually changed.
   *
   * Additionally, a callback can be supplied (onChange) which will activate only if we detect a change. Useful to trigger the actual AJAX
   * paging request.
   *
   * @param {object} newPaging
   * @param {function} onChange
   */
  update (newPaging, onChange = () => {})
  {
    let hasChanged = this.page !== newPaging.page
                      || this.sortBy[0] !== newPaging.sortBy[0]
                      || this.sortDesc[0] !== newPaging.sortDesc[0];

    this.page = newPaging.page;
    this.sortBy = newPaging.sortBy;
    this.sortDesc = newPaging.sortDesc;

    if (hasChanged)
    {
      onChange();
    }
  }
}